'use client';

import React, { useEffect, useRef, useState } from 'react';


interface Package {
  id: number;
  name: string;
  tagline: string;
  category: string;
  tier: 'essential' | 'signature' | 'premium' | 'editorial';
  limited?: boolean;
  popular?: boolean;
  image: string;
  imageAlt: string;
  imagePosition?: string;
  imageScale?: number;
  heightClass: string;
  description: string;
  price: string;
  includes: string[];
}

const PACKAGES: Package[] = [
  {
    id: 1,
    name: 'Event Photography',
    tagline: 'Full event · 4–8 hours',
    category: 'Event',
    tier: 'essential',
    image: '/assets/images/IMG_0082-1786151342564.JPG',
    imageAlt: 'Bride and groom sharing a romantic moment at their wedding ceremony, elegant floral arrangements in the background',
    heightClass: 'h-80',
    description: 'Corporate events, launches, galas, and private celebrations captured beautifully.',
    price: '$900',
    includes: ['Up to 8 hours', '150+ edited images', 'Two-photographer option', 'Online gallery', '48-hr turnaround'],
  },
  {
    id: 2,
    name: 'Family Session',
    tagline: 'Groups · 2 hours',
    category: 'Family',
    tier: 'essential',
    image: 'https://images.unsplash.com/photo-1609220136736-443140cffec6?w=800&q=80',
    imageAlt: 'Happy family of four embracing outdoors in golden hour light, parents and two children laughing together in a meadow',
    heightClass: 'h-96',
    description: 'Real moments, not poses. Outdoor or in-home sessions available.',
    price: '$300',
    includes: ['2-hour session', '50 edited images', 'Online gallery', 'Print release', 'Up to 6 people'],
  },
  {
    id: 3,
    name: 'Real Estate',
    tagline: 'Property · Half day',
    category: 'Real Estate',
    tier: 'essential',
    image: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa',
    imageAlt: 'Bright modern living room interior with large windows, clean lines, and natural light flooding in',
    heightClass: 'h-64',
    description: 'Showcase properties at their best with stunning interior and exterior photography.',
    price: '$600',
    includes: ['4-hour session', '40 edited images', 'Interior & exterior', 'Same-week delivery'],
  },
  {
    id: 4,
    name: 'Graduation',
    tagline: 'Milestone · 1 hour',
    category: 'Graduation',
    tier: 'essential',
    image: 'https://images.unsplash.com/photo-1627556704302-624286467c65?w=800&q=80',
    imageAlt: 'Graduate in cap and gown smiling joyfully outdoors on a bright sunny day, holding diploma with pride',
    heightClass: 'h-[420px]',
    description: 'Celebrate your milestone with stunning graduation portraits on or off campus.',
    price: '$300',
    includes: ['1-hour session', '40 edited images', 'Cap & gown + casual looks', 'Online gallery', 'Print release'],
  },
  {
    id: 5,
    name: 'Professional Headshots',
    tagline: 'Business · 1 hour',
    category: 'Professional',
    tier: 'signature',
    popular: true,
    image: '/assets/images/IMG_0070-1786670572712.JPG',
    imageAlt: 'Professional headshot portrait photo',
    heightClass: 'h-[560px]',
    description: 'Polished headshots for LinkedIn, press kits, team pages, and executive profiles.',
    price: '$150',
    includes: ['1-hour session', '30 edited images', '3 background options', 'Styling guidance', 'Same-week delivery'],
  },
  {
    id: 6,
    name: 'Personal Portraits',
    tagline: 'Individual · 90 min',
    category: 'Personal',
    tier: 'signature',
    image: '/assets/images/Opera_Snapshot_2026-08-13_174859_drive.google.com-1786668567984.png',
    imageAlt: 'Personal portrait photo',
    imagePosition: 'center top',
    imageScale: 1.0,
    heightClass: 'h-[420px]',
    description: 'Clean, timeless portraits at a location of your choice.',
    price: '$225',
    includes: ['90-min session', '25 edited images', 'Online gallery', 'Print release'],
  },
  {
    id: 9,
    name: 'Senior Portraits',
    tagline: 'Seniors · 1 hour',
    category: 'Senior',
    tier: 'signature',
    image: '/assets/images/senior_portrait_photo.png',
    imageAlt: 'High school senior smiling confidently outdoors in golden hour light, warm tones professional portrait',
    heightClass: 'h-[420px]',
    description: 'Celebrate your senior year with stunning portraits that capture your personality and milestone.',
    price: '$175',
    includes: ['1-hour session', '30 edited images', 'Multiple outfit changes', 'Online gallery', 'Print release'],
  },
  {
    id: 7,
    name: 'Custom Package',
    tagline: 'Tailored · Your vision',
    category: 'Custom',
    tier: 'premium',
    image: '/assets/images/IMG_0340-1787367787162.JPG',
    imageAlt: 'Custom photography package portrait session',
    heightClass: 'h-72',
    description: 'Have a unique vision? We build a bespoke package around your exact needs.',
    price: 'Custom',
    includes: ['Flexible hours', 'Tailored deliverables', 'Dedicated photographer', 'Custom licensing', 'Priority booking'],
  },
  {
    id: 8,
    name: 'The Archive',
    tagline: 'Our work speaks.',
    category: 'Portfolio',
    tier: 'editorial',
    image: 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d',
    imageAlt: 'Dramatic editorial portrait with cinematic lighting, subject gazing off-camera, rich shadow detail',
    heightClass: 'h-64',
    description: 'Every frame tells a story.',
    price: '',
    includes: [],
  },
];

const TERMS_SECTIONS = [
  {
    title: 'Booking & Payment',
    body: 'Full payment is required to secure your session date. Refunds are available up to 24 hours before the scheduled shoot time.',
  },
  {
    title: 'Cancellations',
    body: 'Cancellations within 24 hours of the session will be charged the full amount.',
  },
  {
    title: 'Rescheduling',
    body: 'Rescheduling is available up to 24 hours before scheduled shoot.',
  },
  {
    title: 'Image Delivery',
    body: 'You will receive a gallery of edited digital images within 7 days. Exact number depends on the package.',
  },
  {
    title: 'Printing',
    body: 'Request of a physical print order has an additional fee.',
  },
  {
    title: 'Usage',
    body: 'Regal Portraits reserves the right to use any photographs for gallery images, social media advertisements, and example images. Customers will be notified if their photos will be used as such, but are entitled to deny the use of their photos.',
  },
  {
    title: 'Liability',
    body: 'Our liability is limited to the amount paid for the session. We are not responsible for acts of God or circumstances beyond our control.',
  },
  {
    title: 'Agreement',
    body: 'Upon booking of a session, all terms are agreed to by the customer.',
  },
];

interface BookingModalProps {
  pkg: Package | null;
  onClose: () => void;
}

function BookingModal({ pkg, onClose }: BookingModalProps) {
  const [step, setStep] = useState<'select' | 'confirm'>('select');
  const [date, setDate] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [details, setDetails] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [showTerms, setShowTerms] = useState(false);

  if (!pkg) return null;

  const handleConfirm = () => {
    const body = `Name: ${name}\nPackage: ${pkg.name}\nDate + Time: ${date}\nDetails: ${details || 'N/A'}`;
    const mailtoUrl = `mailto:zach@regal-portraits.com?subject=Booking%20Inquiry%20-%20${encodeURIComponent(pkg.name)}&body=${encodeURIComponent(body)}`;
    window.open(mailtoUrl, '_blank');
    onClose();
  };

  return (
    <>
    <div className={`modal-overlay ${pkg ? 'open' : ''}`} onClick={onClose}>
      <div className="modal-box" onClick={(e) => e.stopPropagation()}>
        {step === 'select' ? (
          <>
            <div className="flex items-start justify-between mb-8">
              <div>
                <p className="genre-tag mb-2">{pkg.category}</p>
                <h3 className="font-display font-bold text-2xl" style={{ color: '#E8E4DC' }}>
                  {pkg.name}
                </h3>
                <p className="text-xs mt-1" style={{ color: 'rgba(232,228,220,0.45)', letterSpacing: '0.1em' }}>
                  {pkg.tagline}
                </p>
              </div>
              <button
                onClick={onClose}
                className="text-xs font-bold uppercase tracking-widest p-2 transition-colors"
                style={{ color: 'rgba(232,228,220,0.3)' }}
                onMouseEnter={(e) => ((e.target as HTMLButtonElement).style.color = '#D4A843')}
                onMouseLeave={(e) => ((e.target as HTMLButtonElement).style.color = 'rgba(232,228,220,0.3)')}
              >
                ✕
              </button>
            </div>

            <div className="gold-divider mb-8" />

            {/* What's included */}
            <div className="mb-6">
              <p className="text-[9px] font-bold uppercase tracking-[0.3em] mb-3" style={{ color: 'rgba(139,105,20,0.8)' }}>
                What's Included
              </p>
              <ul className="space-y-2">
                {pkg.includes.map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-xs" style={{ color: 'rgba(232,228,220,0.6)' }}>
                    <span style={{ color: '#D4A843', fontSize: '10px' }}>✦</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Preferred date + time */}
            <div className="mb-6">
              <label className="block text-[9px] font-bold uppercase tracking-[0.3em] mb-2" style={{ color: 'rgba(139,105,20,0.8)' }}>
                Date + Time
              </label>
              <input
                type="text"
                placeholder="e.g. Aug 30, 2026 at 2:00 PM"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="field-dark"
              />
            </div>

            {/* Name */}
            <div className="mb-6">
              <label className="block text-[9px] font-bold uppercase tracking-[0.3em] mb-2" style={{ color: 'rgba(139,105,20,0.8)' }}>
                Name
              </label>
              <input
                type="text"
                placeholder="Your name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="field-dark"
              />
            </div>

            {/* Details */}
            <div className="mb-8">
              <label className="block text-[9px] font-bold uppercase tracking-[0.3em] mb-2" style={{ color: 'rgba(139,105,20,0.8)' }}>
                Details (optional)
              </label>
              <textarea
                placeholder="Describe your vision, location ideas, or any special requests..."
                value={details}
                onChange={(e) => setDetails(e.target.value)}
                rows={3}
                className="field-dark resize-none"
                style={{ width: '100%' }}
              />
            </div>

            {/* Terms Checkbox */}
            <div className="flex items-start gap-3 mb-8">
              <button
                type="button"
                onClick={() => setAgreedToTerms(!agreedToTerms)}
                className="flex-shrink-0 w-4 h-4 mt-0.5 flex items-center justify-center transition-all duration-200"
                style={{
                  border: `1px solid ${agreedToTerms ? '#D4A843' : 'rgba(139,105,20,0.5)'}`,
                  background: agreedToTerms ? 'rgba(212,168,67,0.15)' : 'transparent',
                }}
                aria-label="Agree to terms and conditions"
              >
                {agreedToTerms && (
                  <svg viewBox="0 0 12 12" fill="none" className="w-2.5 h-2.5">
                    <path d="M2 6l3 3 5-5" stroke="#D4A843" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </button>
              <span className="text-xs leading-relaxed" style={{ color: 'rgba(232,228,220,0.55)' }}>
                I agree to all{' '}
                <button
                  type="button"
                  onClick={() => setShowTerms(true)}
                  className="underline underline-offset-2 transition-colors duration-200 cursor-pointer"
                  style={{ color: '#D4A843' }}
                  onMouseEnter={(e) => ((e.currentTarget as HTMLButtonElement).style.color = '#E8C96A')}
                  onMouseLeave={(e) => ((e.currentTarget as HTMLButtonElement).style.color = '#D4A843')}
                >
                  terms and conditions
                </button>
              </span>
            </div>

            {/* Total */}
            <div className="flex justify-between items-center mb-8 px-1">
              <span className="text-xs uppercase tracking-[0.2em]" style={{ color: 'rgba(232,228,220,0.35)' }}>
                Package Price
              </span>
              <span className="font-display font-bold text-2xl" style={{ color: '#D4A843' }}>
                {pkg.price}
              </span>
            </div>

            <button
              className="btn-gold w-full py-4 text-center"
              onClick={() => setStep('confirm')}
              disabled={!date || !name || !agreedToTerms}
              style={{ opacity: !date || !name || !agreedToTerms ? 0.4 : 1 }}
            >
              <span>Continue to Booking</span>
            </button>
          </>
        ) : (
          <>
            <div className="flex items-center justify-between mb-8">
              <h3 className="font-display font-bold text-xl" style={{ color: '#E8E4DC' }}>
                Confirm Booking
              </h3>
              <button
                onClick={() => setStep('select')}
                className="text-xs uppercase tracking-widest"
                style={{ color: 'rgba(232,228,220,0.3)' }}
              >
                ← Back
              </button>
            </div>

            <div className="gold-divider mb-8" />

            <div className="space-y-3 mb-8 p-4" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(232,228,220,0.08)' }}>
              <div className="flex justify-between text-xs">
                <span style={{ color: 'rgba(232,228,220,0.4)' }}>Package</span>
                <span style={{ color: '#E8E4DC' }}>{pkg.name}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span style={{ color: 'rgba(232,228,220,0.4)' }}>Date + Time</span>
                <span style={{ color: '#E8E4DC' }}>{date}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span style={{ color: 'rgba(232,228,220,0.4)' }}>Name</span>
                <span style={{ color: '#E8E4DC' }}>{name}</span>
              </div>
              {details && (
                <div className="flex justify-between text-xs">
                  <span style={{ color: 'rgba(232,228,220,0.4)' }}>Details</span>
                  <span style={{ color: '#E8E4DC', maxWidth: '60%', textAlign: 'right' }}>{details}</span>
                </div>
              )}
            </div>

            <div className="flex justify-between items-center mb-6 px-1">
              <span className="text-xs" style={{ color: 'rgba(232,228,220,0.35)' }}>
                {pkg.name} · {pkg.tagline}
              </span>
              <span className="font-display font-bold text-xl" style={{ color: '#D4A843' }}>
                {pkg.price}
              </span>
            </div>

            <button className="btn-gold w-full py-4" onClick={handleConfirm}>
              <span>Book Now</span>
            </button>

            <p className="text-center text-xs mt-4" style={{ color: 'rgba(232,228,220,0.2)' }}>
              Clicking Book Now will open your email client with the details pre-filled.
            </p>
          </>
        )}
      </div>

      {/* Terms Modal */}
      {showTerms && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0,0,0,0.85)' }}
          onClick={() => setShowTerms(false)}
        >
          <div
            className="relative w-full max-w-lg max-h-[80vh] overflow-y-auto p-8"
            style={{ backgroundColor: '#0f0e0c', border: '1px solid rgba(139,105,20,0.3)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowTerms(false)}
              className="absolute top-4 right-4 transition-opacity duration-200 hover:opacity-70"
              style={{ color: 'rgba(232,228,220,0.4)' }}
              aria-label="Close terms"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <h2
              className="text-lg font-semibold tracking-widest mb-6"
              style={{ color: '#D4A843', fontFamily: 'var(--font-display, serif)' }}
            >
              TERMS & CONDITIONS
            </h2>

            <div className="space-y-5">
              {TERMS_SECTIONS.map(({ title, body }) => (
                <div key={title}>
                  <p className="text-xs font-semibold tracking-widest mb-1" style={{ color: '#D4A843' }}>
                    {title.toUpperCase()}
                  </p>
                  <p className="text-sm leading-relaxed" style={{ color: 'rgba(232,228,220,0.7)' }}>
                    {body}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
}

export default function MasonryGrid() {
  const [selectedPackage, setSelectedPackage] = useState<Package | null>(null);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('revealed');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );

    itemRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, []);

  const essentialPackages = PACKAGES.filter((p) => p.tier === 'essential');
  const signaturePackages = PACKAGES.filter((p) => p.tier === 'signature' || p.tier === 'premium');

  const renderCard = (pkg: Package, index: number, globalIndex: number) => (
    <div
      key={pkg.id}
      ref={(el) => { itemRefs.current[globalIndex] = el; }}
      className="masonry-item reveal"
      style={{ animationDelay: `${index * 0.08}s` }}
    >
      <div
        className="event-card"
        onClick={() => pkg.tier !== 'editorial' && setSelectedPackage(pkg)}
        style={{ borderRadius: '2px' }}
      >
        <div className={`relative ${pkg.heightClass} overflow-hidden`}>
          <img src={pkg.image} alt={pkg.imageAlt} className="absolute inset-0 w-full h-full object-cover" style={{ objectPosition: pkg.imagePosition || 'center center', transform: pkg.imageScale ? `scale(${pkg.imageScale})` : undefined, transformOrigin: pkg.imagePosition === 'center top' ? 'center top' : 'center center' }} />

          {/* Popular badge */}
          {pkg.popular && (
            <div
              className="absolute top-4 left-4 z-10 px-3 py-1"
              style={{ background: 'rgba(11,11,15,0.7)', border: '1px solid rgba(212,168,67,0.5)' }}
            >
              <span className="text-[9px] font-bold uppercase tracking-[0.3em]" style={{ color: '#D4A843' }}>
                Most Popular
              </span>
            </div>
          )}

          {/* Limited badge */}
          {pkg.limited && (
            <div className="absolute top-4 right-4 z-10 sold-out-badge" style={{ borderColor: '#D4A843', color: '#D4A843' }}>
              Limited
            </div>
          )}

          {/* Card content */}
          {pkg.name === 'Custom Package' ? (
            <div
              className="event-card-content"
              style={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-end', justifyContent: 'space-between', gap: '12px', paddingTop: '60px' }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <p className="genre-tag mb-2">{pkg.category}</p>
                <h3
                  className="font-display font-bold leading-tight mb-1"
                  style={{ fontSize: '1.25rem', color: '#E8E4DC' }}
                >
                  {pkg.name}
                </h3>
                <p className="text-xs mb-1" style={{ color: 'rgba(232,228,220,0.45)', letterSpacing: '0.1em' }}>
                  {pkg.tagline}
                </p>
                <p className="text-xs leading-relaxed" style={{ color: 'rgba(232,228,220,0.35)' }}>
                  {pkg.description}
                </p>
              </div>
              <div className="ticket-btn" style={{ flexShrink: 0 }}>
                <button
                  className="btn-gold"
                  onClick={(e) => { e.stopPropagation(); setSelectedPackage(pkg); }}
                >
                  <span>{`Book Now · ${pkg.price}`}</span>
                </button>
              </div>
            </div>
          ) : (
            <div
              className="event-card-content"
              style={pkg.name === 'Personal Portraits' ? { left: 'auto', right: 0, width: '55%', textAlign: 'left' } : undefined}
            >
              <p className="genre-tag mb-2">{pkg.category}</p>
              <h3
                className="font-display font-bold leading-tight mb-1"
                style={{ fontSize: pkg.tier === 'editorial' ? '1.1rem' : '1.25rem', color: '#E8E4DC' }}
              >
                {pkg.name}
              </h3>
              <p className="text-xs mb-3" style={{ color: 'rgba(232,228,220,0.45)', letterSpacing: '0.1em' }}>
                {pkg.tagline}
              </p>
              <p className="text-xs mb-4 leading-relaxed" style={{ color: 'rgba(232,228,220,0.35)' }}>
                {pkg.description}
              </p>

              {pkg.tier !== 'editorial' && (
                <div className="ticket-btn">
                  <button
                    className="btn-gold"
                    onClick={(e) => { e.stopPropagation(); setSelectedPackage(pkg); }}
                  >
                    <span>{`Book Now · ${pkg.price}`}</span>
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <>
      <section id="packages" className="px-4 md:px-8 py-20 max-w-7xl mx-auto">
        {/* Section label */}
        <div className="flex items-center gap-6 mb-12">
          <div className="gold-divider flex-1" />
          <span className="text-[9px] font-bold uppercase tracking-[0.5em]" style={{ color: '#8B6914', whiteSpace: 'nowrap' }}>
            Photography Packages
          </span>
          <div className="gold-divider flex-1" />
        </div>

        {/* All packages in one unified grid */}
        <div className="masonry-grid">
          {[...essentialPackages, ...signaturePackages].map((pkg, i) => renderCard(pkg, i, i))}
        </div>

      </section>

      {/* Booking Modal */}
      {selectedPackage && (
        <BookingModal pkg={selectedPackage} onClose={() => setSelectedPackage(null)} />
      )}
    </>
  );
}
